import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Search, Package, User, ArrowLeft, RotateCcw, Loader2, ScanLine, Smartphone, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { UserDetailSheet } from "@/components/UserDetailSheet";

interface ScanResult {
  box: {
    id: string;
    box_number: string;
    name: string;
    location_status: string;
    created_at: string;
    stored_at: string | null;
  };
  customer: {
    id: string;
    full_name: string | null;
    phone: string;
    email?: string;
  };
  returnRequest: {
    id: string;
    status: string;
    requested_at: string;
  } | null;
}

interface UserData {
  id: string;
  email: string;
  full_name: string | null;
  phone: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  address_line1: string | null;
  address_line2?: string | null;
  created_at: string;
  signup_source: string | null;
  use_case: string | null;
  billing_start_date: string | null;
  box_slots_purchased?: number | null;
  box_slots_used?: number | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  total_boxes: number;
  total_items: number;
  monthly_revenue: number;
  months_stored: number | null;
  status: 'active' | 'inactive' | 'never_stored';
  stored_boxes: number;
}

const AdminFacilityScan = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [manualInput, setManualInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [userSheetOpen, setUserSheetOpen] = useState(false);

  // Auto-lookup if boxNumber is in URL
  useEffect(() => {
    const boxNumber = searchParams.get("boxNumber");
    if (boxNumber) {
      setManualInput(boxNumber);
      lookupBox(boxNumber);
    }
  }, [searchParams]);

  const lookupBox = async (boxNumber: string) => {
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      // Query box with profile
      const { data: boxData, error: boxError } = await supabase
        .from("boxes")
        .select(`
          id,
          box_number,
          name,
          location_status,
          created_at,
          stored_at,
          user_id,
          profiles!inner (
            id,
            full_name,
            phone
          )
        `)
        .ilike("box_number", boxNumber)
        .maybeSingle();

      if (boxError) throw boxError;

      if (!boxData) {
        setError(`Box "${boxNumber}" not found`);
        return;
      }

      // Get return request if exists
      const { data: returnData } = await supabase
        .from("return_requests")
        .select("id, status, requested_at")
        .eq("box_id", boxData.id)
        .order("requested_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      // Get user data via edge function for email and full user details
      let fullUserData: UserData | null = null;
      try {
        const { data: userData } = await supabase.functions.invoke("admin-list-users", {
          body: { search: boxData.profiles.phone, limit: 1 }
        });
        if (userData?.users?.[0]) {
          fullUserData = userData.users[0];
        }
      } catch (e) {
        console.log("Could not fetch full user data:", e);
      }

      // Store full user data for the sheet
      setSelectedUser(fullUserData);

      setResult({
        box: {
          id: boxData.id,
          box_number: boxData.box_number,
          name: boxData.name,
          location_status: boxData.location_status,
          created_at: boxData.created_at,
          stored_at: boxData.stored_at,
        },
        customer: {
          id: boxData.profiles.id,
          full_name: boxData.profiles.full_name,
          phone: boxData.profiles.phone,
          email: fullUserData?.email,
        },
        returnRequest: returnData,
      });
    } catch (err: any) {
      console.error("Lookup error:", err);
      setError(err.message || "Failed to lookup box");
      toast({
        title: "Lookup Failed",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualLookup = () => {
    if (manualInput.trim()) {
      lookupBox(manualInput.trim());
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      home: "outline",
      in_transit: "secondary",
      stored: "default",
      returned: "destructive",
    };
    return <Badge variant={variants[status] || "outline"}>{status.replace("_", " ")}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Button variant="ghost" onClick={() => navigate("/admin")} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <ScanLine className="h-8 w-8" />
            Facility Scanner
          </h1>
          <p className="text-muted-foreground">
            Scan or enter a box number to look up box and customer details
          </p>
        </div>

        {/* Tip Card */}
        <Card className="mb-6 border-primary/20 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Smartphone className="h-5 w-5 mt-0.5 text-primary" />
              <div>
                <p className="font-medium text-sm">Tip: Use your phone's camera</p>
                <p className="text-muted-foreground text-sm">
                  Open your camera app and scan a box QR code. It will automatically open this page with the box details.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Manual Input Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Lookup Box</CardTitle>
            <CardDescription>Enter box number to look up details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter box number (e.g., SCOTTPROD-001)"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleManualLookup()}
              />
              <Button onClick={handleManualLookup} disabled={!manualInput.trim() || isLoading}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {isLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Looking up box...</p>
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Card className="border-destructive">
            <CardContent className="py-8 text-center">
              <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-destructive font-medium">{error}</p>
              <p className="text-muted-foreground text-sm mt-2">
                Check the box number and try again
              </p>
            </CardContent>
          </Card>
        )}

        {/* Result Card */}
        {result && !isLoading && (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    {result.box.box_number}
                  </CardTitle>
                  <CardDescription>{result.box.name}</CardDescription>
                </div>
                {getStatusBadge(result.box.location_status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Customer Info - Clickable */}
              <button
                onClick={() => selectedUser && setUserSheetOpen(true)}
                className="w-full flex items-start gap-3 p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors text-left group"
                disabled={!selectedUser}
              >
                <User className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{result.customer.full_name || "No name"}</p>
                  {result.customer.email && (
                    <p className="text-sm text-muted-foreground truncate">{result.customer.email}</p>
                  )}
                  <p className="text-sm text-muted-foreground">{result.customer.phone}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors mt-0.5" />
              </button>

              <Separator />

              {/* Return Request Status */}
              <div className="flex items-center gap-3">
                <RotateCcw className="h-5 w-5 text-muted-foreground" />
                {result.returnRequest ? (
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Return Requested</span>
                      <Badge variant={result.returnRequest.status === "pending" ? "destructive" : "secondary"}>
                        {result.returnRequest.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {new Date(result.returnRequest.requested_at).toLocaleDateString()}
                    </p>
                  </div>
                ) : (
                  <span className="text-muted-foreground">No return requested</span>
                )}
              </div>

              {/* Actions */}
              {result.returnRequest && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate("/admin/return-requests")}
                >
                  View in Return Requests →
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* User Detail Sheet */}
        <UserDetailSheet
          user={selectedUser}
          open={userSheetOpen}
          onOpenChange={setUserSheetOpen}
        />
      </div>
    </div>
  );
};

export default AdminFacilityScan;
